import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';

// INTERFACES

// LIB
import Swal from 'sweetalert2';

// SERVICES
import { PricingLevelService } from './../../../core/pricing-level/pricing-level.service';
import { PricingOverrideService } from './../../../core/pricing-override/pricing-override.service';
import { PricingScheduleService } from './../../../core/pricing-schedule/pricing-schedule.service';
import { PricingTierService } from './../../../core/pricing-tier/pricing-tier.service';
import {
  PricingLevelInterface,
  PricingOverrideInterface,
  PricingScheduleInterface,
  PricingTierInterface,
} from './../../pricing/interfaces/pricing.interface';

@Component({
  selector: 'cnc-rfq-pricing',
  templateUrl: './rfq-pricing.component.html',
  styleUrls: ['./rfq-pricing.component.scss'],
})
export class RfqPricingComponent implements OnInit, OnDestroy {
  @Input()
  rfq :any;

  pricingSchedules$ :Observable<PricingScheduleInterface[]>;
  pricingOverrides$ :Observable<PricingOverrideInterface[]>;
  pricingSchedules :PricingScheduleInterface[];
  pricingSchedulesWithOverrides :PricingScheduleInterface[];
  pricingTiers$ :Observable<PricingTierInterface[]>;
  pricingLevels$ :Observable<PricingLevelInterface[]>;
  selectedPricingSchedule :PricingScheduleInterface;
  submittingForm$ :Observable<boolean>;

  private onDestroy$ = new Subject();

  constructor(
    private pricingScheduleService :PricingScheduleService,
    private pricingTierService :PricingTierService,
    private pricingLevelService :PricingLevelService,
    private snackBar :MatSnackBar,
    private pricingOverrideService :PricingOverrideService,
  ) {}

  ngOnInit() :void {
    this.initActions();
    this.getDataFromService();
    this.getPricingSchedules().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  trackById(schedule :PricingScheduleInterface) :number {
    return schedule.id;
  }

  onSelectPricingSchedule(event :MatSelectChange) :void {
    this.selectedPricingSchedule = event.value;
    this.pricingOverrideService.get({
      limit: '1000',
      rfqId: this.rfq.id,
      priceScheduleId: this.selectedPricingSchedule.id.toString(),
    });
  }

  tierOverride(data :{
    priceOverride :PricingOverrideInterface;
    price :number;
    rfqId :number;
    priceScheduleTierId :number;
    priceLevelId :number;
  }) :void {
    const { priceOverride, price, rfqId, priceScheduleTierId, priceLevelId } = data;

    if (priceOverride) {
      this.pricingOverrideService
        .update({
          id: priceOverride.id,
          payload: {
            rfqId,
            priceScheduleTierId,
            priceLevelId,
            price,
          },
        })
        .pipe(
          catchError((response) => {
            Swal.fire('Error', response.error, 'error');

            return throwError(response.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe(() => {
          this.snackBar.open('Price succussfully overriden.', 'Success', {
            duration: 3 * 1000, // 3 sec
          });
        });
    } else {
      this.pricingOverrideService
        .create({ price, rfqId, priceScheduleTierId, priceLevelId })
        .pipe(
          catchError((response) => {
            Swal.fire('Error', response.error, 'error');

            return throwError(response.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe((response) => {
          this.snackBar.open('Price succussfully overriden.', 'Success', {
            duration: 3 * 1000, // 3 sec
          });
        });
    }
  }

  submit() :void {
    this.pricingScheduleService
      .attachToRfq({
        priceScheduleId: this.selectedPricingSchedule.id,
        rfqId: this.rfq.id,
      })
      .pipe(
        catchError((response) => {
          Swal.fire('Error', response.error, 'error');

          return throwError(response.error);
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe((response) => {
        this.rfq.price_schedule_id = this.selectedPricingSchedule.id;
        this.snackBar.open(response.success, 'Success', {
          duration: 3 * 1000, // 3 sec
        });
      });
  }

  private initActions() :void {
    this.pricingScheduleService.get({ relations: 'priceScheduleTiers' });
    this.pricingTierService.get({});
    this.pricingLevelService.get({});
    this.pricingOverrideService.get({
      limit: '1000',
      rfqId: this.rfq.id,
      priceScheduleId: this.rfq.price_schedule_id,
    });
  }

  private getDataFromService() :void {
    this.pricingSchedules$ = this.pricingScheduleService.pricingSchedules$;
    this.pricingOverrides$ = this.pricingOverrideService.pricingOverrides$;
    this.pricingTiers$ = this.pricingTierService.pricingTiers$;
    this.pricingLevels$ = this.pricingLevelService.pricingLevels$;
    this.submittingForm$ = this.pricingScheduleService.submittingForm$;
  }

  private initSelectedPricingSchedule({ scheduleId, schedules }) :void {
    const currentSchedule = schedules.find(
      (schedule :PricingScheduleInterface) => schedule.id === scheduleId,
    );

    setTimeout(() => {
      this.selectedPricingSchedule = currentSchedule;
    });
  }

  private getPricingSchedules() :Observable<PricingScheduleInterface[]> {
    return this.pricingSchedules$.pipe(
      tap((schedules :PricingScheduleInterface[]) => {
        this.pricingSchedules = schedules;

        // INIT SELECTED PRICING SCHEDULE
        if (this.rfq.price_schedule_id && schedules?.length) {
          this.initSelectedPricingSchedule({
            scheduleId: this.rfq.price_schedule_id,
            schedules,
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  detachPricingSchedule() :void {
    Swal.fire({
      title: 'Confirmation',
      text: 'Detach pricing schedule from SKU?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, detach it!',
    }).then((result) => {
      if (result.value) {
        this.pricingScheduleService
          .detachFromRfq({
            rfqId: this.rfq.id,
          })
          .pipe(
            catchError((response) => {
              Swal.fire('Error', response.error, 'error');

              return throwError(response.error);
            }),
            takeUntil(this.onDestroy$),
          )
          .subscribe((response) => {
            this.rfq.price_schedule_id = null;
            this.snackBar.open(response.success, 'Success', {
              duration: 3 * 1000, // 3 sec
            });
            this.selectedPricingSchedule = undefined;
          });
      }
    });
  }
}
